import { useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { prepareUploadDataUrl, formatBytes } from '../utils/imageUpload';
import { useUploadStatusStore } from '../stores/uploadStatusStore';
import { toast } from 'sonner';

/**
 * File input that compresses images + validates type/size before handing the
 * base64 data URL to `onUpload` (or the raw File to `onFileUpload` for
 * multipart paths). Shows an inline "Processing…" spinner while the work
 * runs so users don't double-click, a size-comparison success toast, and a
 * persistent "✓ filename uploaded" confirmation below the input.
 *
 * Props:
 *   onUpload(dataUrl)        — JSON/base64 path (default).
 *   onFileUpload(file)       — multipart path: receives the original File so
 *                              caller can POST as multipart/form-data.
 *   allowPdf, accept, disabled, className, data-testid.
 */
export const UploadInput = ({
  onUpload,
  onFileUpload,
  allowPdf = true,
  accept = 'image/*',
  disabled = false,
  className = '',
  ...rest
}) => {
  const [busy, setBusy] = useState(false);
  const [lastUpload, setLastUpload] = useState(null); // { name, bytes }
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    useUploadStatusStore.getState().start();
    try {
      if (onFileUpload) {
        // Multipart path — caller handles compression/upload itself or uses raw bytes.
        await onFileUpload(file);
        setLastUpload({ name: file.name, bytes: file.size });
        toast.success(`Uploaded ${formatBytes(file.size)}`);
      } else {
        const { dataUrl, originalBytes, finalBytes, wasCompressed } =
          await prepareUploadDataUrl(file, { allowPdf });
        await onUpload(dataUrl);
        setLastUpload({ name: file.name, bytes: finalBytes });
        if (wasCompressed && originalBytes > finalBytes) {
          const saved = Math.round(((originalBytes - finalBytes) / originalBytes) * 100);
          toast.success(
            `Compressed ${formatBytes(originalBytes)} → ${formatBytes(finalBytes)} (${saved}% smaller)`
          );
        } else if (finalBytes) {
          toast.success(`Uploaded (${formatBytes(finalBytes)})`);
        }
      }
    } catch (err) {
      // Distinguish processing errors from caller-thrown server errors:
      // server errors already toasted by the caller; we just reset the indicator.
      if (err && err.message && !err.response) {
        toast.error(err.message);
      }
      setLastUpload(null);
    } finally {
      setBusy(false);
      useUploadStatusStore.getState().end();
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled || busy}
          onChange={handleChange}
          {...rest}
        />
        {busy && (
          <div className="absolute inset-y-0 right-2 flex items-center gap-1.5 pointer-events-none">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Processing…</span>
          </div>
        )}
      </div>
      {!busy && lastUpload && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-green-700" data-testid="upload-success-indicator">
          <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            <span className="font-medium">{lastUpload.name}</span>
            <span className="text-green-600"> uploaded ({formatBytes(lastUpload.bytes)})</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default UploadInput;
